'use client';
import { Modal } from '../Modal/Modal';
import { Text } from '@/ui-components/atoms/Text/Text';
import styles from './contentcardmodal.module.scss';
import { ReactNode, useRef, useState } from 'react';
import { FocusTrap } from 'focus-trap-react';
import useClickOutside from '@/hooks/useClickOutside';
import { AudioPlayer } from '@/ui-components/atoms/AudioPlayer/AudioPlayer';
import { AudioProps, ContentCardTheme } from './ContentCard.type';
import { StandardVideo } from '@/ui-components/molecules/Media/StandardVideo/StandardVideo';
import { AspectRatio } from '@/ui-components/atoms/AspectRatio/AspectRatio';
import { RatioBreakpoints } from '@/ui-components/atoms/AspectRatio/AspectRatio.type';
import { LinkWrapperIfHref } from '@/ui-components/atoms/LinkWrapperIfHref/LinkWrapperIfHref';
import { LinkProps } from '@/ui-components/atoms/Link/Link';

interface ContentCardModalProps {
    open: boolean;
    onClose: () => void;
    dataLocalpagename?: string;
    eyebrow?: string;
    title?: ReactNode;
    image?: React.ReactElement<typeof HTMLImageElement>;
    copyright?: string;
    video?: React.ReactElement<typeof StandardVideo>;
    body?: ReactNode;
    audio?: AudioProps;
    linkProps?: LinkProps;
    theme: ContentCardTheme;
    mobileLayout: ContentCardTheme;
    smallVerticalMedia?: boolean;
}

export const ContentCardModal = ({
    open,
    onClose,
    dataLocalpagename,
    eyebrow,
    title,
    image,
    copyright,
    video,
    body,
    audio,
    linkProps,
    theme,
    mobileLayout,
    smallVerticalMedia,
}: ContentCardModalProps) => {
    const MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR = '#onetrust-consent-sdk';
    const [focusPause, setFocusPause] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useClickOutside(ref, {}, (state, event) => {
        if (event) {
            if (event.target) {
                const target = event.target as HTMLElement;
                const closest = target.closest(MODAL_CLICK_THROUGH_EXCEPTIONS_SELECTOR);

                setFocusPause(!!closest);
            }
        }
    });

    let ratioCols: RatioBreakpoints = { sm: '4-3', md: '16-9' };
    if (theme === 'horizontal' && mobileLayout === 'horizontal') {
        ratioCols = { sm: '4-3' };
    } else if (theme === 'horizontal' && smallVerticalMedia) {
        ratioCols = { sm: '4-3', md: '16-9' };
    }

    function stopLinkClick(event: React.MouseEvent<HTMLElement>) {
        event.preventDefault();
    }

    return (
        <FocusTrap
            active={open}
            paused={focusPause}
            focusTrapOptions={{
                allowOutsideClick: true,
            }}
        >
            <Modal
                open={open}
                onClose={onClose}
                className={styles.modal}
                variant="medium"
                dataLocalpagename={dataLocalpagename}
            >
                <div ref={ref} className={styles.content}>
                    {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}

                    {title && (
                        <Text tag="div" variant="h4" className={styles.title}>
                            {title}
                        </Text>
                    )}

                    {!video && (
                        <div className={styles.imageContainer}>
                            {image}
                        </div>
                    )}
                    {video && (
                        <div onClick={stopLinkClick}>
                            <AspectRatio cols={ratioCols} className={styles.videoContainer}>
                                {video}
                            </AspectRatio>
                        </div>
                    )}
                    {copyright?.trim() && (
                        <Text tag="div" variant="text-small" className={styles.copyright}>
                            {copyright}
                        </Text>
                    )}

                {body && <div className={styles.body}>{body}</div>}

                {(linkProps && linkProps.children) && 
                    <LinkWrapperIfHref link={linkProps} className={styles.cardLink}>
                        <span className={styles.link}>
                                {linkProps.children}
                        </span>
                    </LinkWrapperIfHref>
                }

                {audio?.source && (
                    <div className={styles.audioPlayerWrapper}>
                        <AudioPlayer
                            {...audio}
                            isActive={true}
                            onActivate={() => {}}
                            name={typeof title === 'string' ? title : ''}
                        />
                    </div>
                )}
                </div>
            </Modal>
        </FocusTrap>
    );
};